import { type FieldErrors } from 'react-hook-form';

import { flattenErrorPaths } from '../error-paths/error-paths';
import { localizedPath } from '../localized-form/localized-form';
import { type ResolvedSection } from '../resolve-sections/resolve-sections';
import { type Locale } from '@aysar/contracts';

/**
 * Which sections currently hold a rejected field.
 *
 * A collapsible section reads this and refuses to stay folded, for the same
 * reason the locale tab wears an error marker: an error the operator cannot see
 * turns Save into a button that silently does nothing.
 *
 * A translatable field is matched in the locale ON SCREEN as well as in every
 * other one — the section is where the problem is either way, and folding it
 * away because the failure is in the English translation would hide it twice.
 */
export function invalidSectionIdsOf(
  sections: readonly ResolvedSection[],
  errors: FieldErrors,
  locales: readonly Locale[],
): readonly string[] {
  const invalidPaths = flattenErrorPaths(errors).map((error) => error.path);
  if (invalidPaths.length === 0) return [];

  /**
   * A PREFIX match, not an equality one.
   *
   * `flattenErrorPaths` emits leaves, and a field whose value is an object — a
   * `custom` policy, a `key-value` map, a `json` document — fails at
   * `<name>.<member>`. Comparing for equality left every one of those sections
   * unmarked, which for a collapsible one means it stays folded over the error
   * it exists to reveal.
   */
  const isRejected = (path: string) =>
    invalidPaths.some((each) => each === path || each.startsWith(`${path}.`));

  return sections
    .filter((section) =>
      section.fields.some((field) =>
        field.isTranslatable === true
          ? locales.some((locale) => isRejected(localizedPath(field.name, locale)))
          : isRejected(field.name),
      ),
    )
    .map((section) => section.id);
}
